import { Box, Grid, GridItem, Heading, useBreakpointValue, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useHomePageColors } from '@/hooks/useHomePageColors';
import TeamSelect from '../../components/TeamSelect';
import { Country, type CountryCode } from '@/models/country';
import { HomeHeading } from './HomeHeading';
import {
  HOME_DEMO_SELECTED_INTERMEDIATES,
  HOME_DEMO_SELECTED_OUTSIDERS,
  HOME_DEMO_SELECTED_SEEDS,
} from './homeDemoExample';

/** WC 2026 seeds (12) — same order as `country.ts` wc-26. */
const SEEDS: CountryCode[] = [
  'AR',
  'BE',
  'BR',
  'CA',
  'GB_ENG',
  'FR',
  'DE',
  'MX',
  'NL',
  'PT',
  'ES',
  'US',
];

/** WC 2026 intermediates (24). */
const INTERMEDIATES: CountryCode[] = [
  'ALG',
  'AU',
  'AT',
  'CO',
  'HR',
  'EC',
  'EGY',
  'IR',
  'CIV',
  'JP',
  'MA',
  'NO',
  'PA',
  'PAR',
  'QA',
  'SA',
  'GB_SCT',
  'SN',
  'ZA',
  'KR',
  'CH',
  'TN',
  'UZB',
  'UY',
];

/** WC 2026 outsiders (12). */
const OUTSIDERS: CountryCode[] = ['CPV', 'CUW', 'GH', 'HT', 'JOR', 'NZ', 'BIH', 'SE', 'TR', 'CZ', 'COD', 'IRQ'];

/** Desktop: 6 columns; phone: 4 for readable tap targets. */
const GRID_COLUMNS_DESKTOP = 6;
const GRID_COLUMNS_MOBILE = 4;

const ChooseSeedsSection: React.FC = () => {
  const { surfaceMuted } = useHomePageColors();

  return (
    <VStack
      py='36px'
      px={{
        base: '1em',
        sm: '2em',
        md: '24px',
      }}
      bgColor={surfaceMuted}
      borderRadius='8px'
      justifyContent='center'
      w='full'
      spacing={6}
    >
      <HomeHeading
        mb={0}
        width={{
          base: '90%',
          sm: '80%',
          md: '70%',
          lg: '70%',
          xl: '70%',
        }}
      >
        Choose Only 4 Seeds and At Least 2 Outsiders
      </HomeHeading>
      <VStack w='full' spacing={8} align='stretch'>
        <Teams heading='Seeds' data={SEEDS} selectedTeams={[...HOME_DEMO_SELECTED_SEEDS]} />
        <Teams
          heading='Intermediates'
          data={INTERMEDIATES}
          selectedTeams={[...HOME_DEMO_SELECTED_INTERMEDIATES]}
        />
        <Teams heading='Outsiders' data={OUTSIDERS} selectedTeams={[...HOME_DEMO_SELECTED_OUTSIDERS]} />
      </VStack>
    </VStack>
  );
};

export default ChooseSeedsSection;

const Teams = ({
  heading,
  data,
  selectedTeams,
}: {
  heading: string;
  data: CountryCode[];
  selectedTeams: CountryCode[];
}) => {
  const { heading: headingColor } = useHomePageColors();
  const columns = useBreakpointValue({
    base: GRID_COLUMNS_MOBILE,
    md: GRID_COLUMNS_DESKTOP,
  }) ?? GRID_COLUMNS_MOBILE;

  return (
    <Box w='full'>
      <Heading
        mb={2}
        textAlign='left'
        fontStyle='italic'
        fontWeight={600}
        color={headingColor}
        fontSize={{
          base: '16px',
          md: '19px',
        }}
      >
        {heading}
      </Heading>
      <Grid
        w='full'
        templateColumns={`repeat(${columns}, minmax(0, 1fr))`}
        gap={{ base: 2, md: 2.5 }}
      >
        {data.map((team) => {
          const selected = selectedTeams.includes(team);
          return (
            <GridItem key={team} minW={0}>
              <TeamSelect
                countryCode={Country[team]}
                selected={selected}
                nonSelectable={true}
                compact
              />
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};
